/**
 * Resultado do pedido de cancelamento (#6509): o cliente saiu (solicitação "concluída") ou ficou
 * (solicitação "cancelada", a desistência). Ao marcar a desistência, o motivo agora vem de uma
 * lista fechada — o que fez o cliente ficar —, gravado no começo de `concluida_motivo`
 * ("Resolvemos o problema que motivou o pedido: cobrança em dobro"), sem coluna nova.
 *
 * Retenção = pedidos em que o cliente ficou ÷ pedidos encerrados no período (meta 30%). Os pedidos
 * da planilha importada ficam de fora: ela só trouxe cancelamentos concluídos, sem as desistências.
 *
 *   node --experimental-strip-types --test src/lib/__tests__/resultadoCancelamento.test.ts
 */

export const META_RETENCAO = 0.3;

export interface MotivoRetencao { codigo: string; rotulo: string; exigeDetalhe?: boolean }

export const MOTIVOS_RETENCAO: readonly MotivoRetencao[] = [
  { codigo: 'desconto', rotulo: 'Oferecemos desconto ou condição melhor' },
  { codigo: 'problema_resolvido', rotulo: 'Resolvemos o problema que motivou o pedido' },
  { codigo: 'novo_endereco', rotulo: 'Levou a assinatura para o novo endereço' },
  { codigo: 'regras_contrato', rotulo: 'Entendeu as regras do contrato (multa, aviso prévio)' },
  { codigo: 'desistiu_sozinho', rotulo: 'Desistiu sem ação nossa' },
  { codigo: 'outro', rotulo: 'Outro motivo', exigeDetalhe: true },
];

const PORCODIGO = new Map(MOTIVOS_RETENCAO.map(m => [m.codigo, m]));
export const motivoRetencaoPorCodigo = (codigo: string | null | undefined) => (codigo ? PORCODIGO.get(codigo) ?? null : null);

/** Texto gravado em `concluida_motivo`: "Rótulo" ou "Rótulo: detalhe". */
export function textoDaRetencao(codigo: string, detalhe: string): string {
  const m = motivoRetencaoPorCodigo(codigo);
  const d = detalhe.trim();
  if (!m) return d;
  return d ? `${m.rotulo}: ${d}` : m.rotulo;
}

export function faltaNaRetencao(codigo: string | null | undefined, detalhe: string): string | null {
  const m = motivoRetencaoPorCodigo(codigo);
  if (!m) return 'Escolha por que o cliente ficou.';
  if (m.exigeDetalhe && !detalhe.trim()) return 'Descreva por que o cliente ficou.';
  return null;
}

/** Motivo da retenção pelo começo do texto; texto livre de antes da lista volta null. */
export function motivoDaRetencao(texto: string | null | undefined): MotivoRetencao | null {
  const t = (texto ?? '').trim();
  if (!t) return null;
  return MOTIVOS_RETENCAO.find(m => t === m.rotulo || t.startsWith(`${m.rotulo}:`)) ?? null;
}

export interface PedidoEncerrado {
  status: string | null;
  concluida_em: string | null;
  concluida_motivo: string | null;
  origem: string | null;
}

export interface RetencaoDoPeriodo {
  encerrados: number;
  ficaram: number;
  sairam: number;
  taxa: number | null;
  porMotivo: { motivo: MotivoRetencao | null; qtd: number }[];
}

/** Pedidos encerrados entre `inicio` (incluso) e `fim` (exclusivo), sem os da planilha importada. */
export function retencaoDoPeriodo(pedidos: readonly PedidoEncerrado[], inicio: Date, fim: Date): RetencaoDoPeriodo {
  const doPeriodo = pedidos.filter(p => {
    if (p.origem === 'importacao') return false;
    if (p.status !== 'concluida' && p.status !== 'cancelada') return false;
    const t = p.concluida_em ? Date.parse(p.concluida_em) : NaN;
    return Number.isFinite(t) && t >= inicio.getTime() && t < fim.getTime();
  });
  const ficaram = doPeriodo.filter(p => p.status === 'cancelada');
  const conta = new Map<string, { motivo: MotivoRetencao | null; qtd: number }>();
  for (const p of ficaram) {
    const m = motivoDaRetencao(p.concluida_motivo);
    const k = m?.codigo ?? '__sem__';
    const g = conta.get(k) ?? { motivo: m, qtd: 0 };
    g.qtd++;
    conta.set(k, g);
  }
  return {
    encerrados: doPeriodo.length,
    ficaram: ficaram.length,
    sairam: doPeriodo.length - ficaram.length,
    taxa: doPeriodo.length ? ficaram.length / doPeriodo.length : null,
    porMotivo: [...conta.values()].sort((a, b) => b.qtd - a.qtd),
  };
}
